import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

interface CompanyData {
  companyName: string;
  domain?: string;
  contactId?: number;
  linkedin?: string;
  companyIdentifier?: string;
  industry?: string;
  numberOfEmployees?: string;
  revenue?: string;
  aboutCompany?: string;
  country?: string;
  region?: string;
  city?: string;
  address?: string;
  zipCode?: string;
}

@Component({
  selector: 'app-add-company-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule
  ],
  templateUrl: './add-company-modal.component.html',
  styleUrl: './add-company-modal.component.scss'
})
export class AddCompanyModalComponent implements OnInit, OnChanges {
  @Input() isVisible: boolean = false;
  @Input() contacts: any[] = [];
  @Input() company: any = null; // For edit mode
  @Input() mode: 'create' | 'edit' = 'create';
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<CompanyData>();
  @Output() deleteCompanyContact = new EventEmitter<number>();

  companyForm: FormGroup;
  loading: boolean = false;
  existingContacts: any[] = [];

  constructor(private fb: FormBuilder) {
    this.companyForm = this.createForm();
  }

  ngOnInit(): void {
    // If in edit mode, populate the form
    if (this.mode === 'edit' && this.company) {
      this.populateForm();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to company, mode, or isVisible inputs
    if (changes['company'] || changes['mode'] || changes['isVisible']) {
      // If modal becomes visible and we're in edit mode, populate the form
      if (this.isVisible && this.mode === 'edit' && this.company) {
        this.populateForm();
      } else if (this.isVisible && this.mode === 'create') {
        this.companyForm.reset();
        this.existingContacts = [];
      }
    }
  }

  private createForm(): FormGroup {
    return this.fb.group({
      companyName: ['', [Validators.required]],
      domain: ['', [Validators.pattern(/^([a-zA-Z0-9]+(-[a-zA-Z0-9]+)*\.)+[a-zA-Z]{2,}$/)]],
      contactId: ['']
    });
  }


  closeModal(): void {
    this.resetForm();
    this.close.emit();
  }

  saveCompany(): void {
    if (this.companyForm.valid) {
      this.loading = true;

      const formValue = this.companyForm.value;
      const companyData: CompanyData = {
        companyName: formValue.companyName,
        domain: formValue.domain || undefined,
        contactId: formValue.contactId || undefined
      };

      // Simulate API call delay
      setTimeout(() => {
        this.save.emit(companyData);
        this.loading = false;
        this.resetForm();
        this.closeModal();
      }, 1000);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.companyForm.controls).forEach(key => {
        this.companyForm.controls[key].markAsTouched();
      });
    }
  }

  private resetForm(): void {
    this.companyForm.reset();
    this.loading = false;
    this.existingContacts = [];
  }

  removeContact(companyContactId: number): void {
    if (confirm('Are you sure you want to remove this contact association?')) {
      this.deleteCompanyContact.emit(companyContactId);
      // Remove from local list for immediate UI update
      this.existingContacts = this.existingContacts.filter(cc => cc.id !== companyContactId);
    }
  }

  private populateForm(): void {
    if (this.company) {
      // Store existing contacts for display
      this.existingContacts = this.company.company_contacts || [];

      // Extract domain from URL if it exists
      let domain = '';
      if (this.company.url) {
        try {
          const url = this.company.url.startsWith('http') ? this.company.url : `https://${this.company.url}`;
          domain = new URL(url).hostname;
        } catch (error) {
          domain = this.company.url;
        }
      }

      this.companyForm.patchValue({
        companyName: this.company.name || '',
        domain: domain || '',
        contactId: '' // Empty in edit mode as we show existing contacts separately
      });
    }
  }
}
